import {IAnimation} from './animation.interface';
import {AnimationLoadingGNameEnum} from './animation-name.enum';
import {AnimationFillModeEnum} from './animation-fill.enum';
import {AnimationTimingFunctionsEnum} from './animation-timing-functions.enum';
import {AnimationPlayStateEnum} from './animation-play-state.enum';
import {AnimationDirectionEnum} from './animation-direction.enum';
import {AnimationIterationCountEnum} from './animation-iteration-count.enum';

//#region INTERFACE ISharedLoadingGAnimation
/**
 * Describes the configuration options for a shared SVG loading `<g>` animation element.
 *
 * @extends Object
 * @extends IAnimation
 */
export interface ISharedLoadingGAnimation extends Object, IAnimation {
  /**
   * The dash pattern used for the stroke animation.
   * @description Controls how the stroke of the SVG path is dashed.
   *              Higher values simulate a longer dashed line.
   *
   * @type {string}
   * @default '1000'
   * @example '1000'
   */
  strokeDashArray: string;

  /**
   * Offset for the dashed stroke pattern.
   * @description Controls how far into the dash pattern the stroke rendering should start.
   *
   * @type {string}
   * @default '1000'
   * @example '1000'
   */
  strokeDashOffset: string;

  /**
   * Fill color for the path.
   * @description Accepts any valid CSS color value including variables.
   *
   * @type {string}
   * @optional
   * @default 'transparent' or context-defined
   * @example '#ffffff'
   */
  fillColour?: string;

  /**
   * Color of the stroke.
   * @description Accepts any valid CSS color value including variables.
   *
   * @type {string}
   * @optional
   * @default 'var(--global-colour-text)' or 'var(--global-colour-primary)' depending on context
   * @example '#ff0000'
   */
  strokeColour?: string;

  /**
   * End value for stroke-dashoffset animation.
   * @description Defines the final offset value for the stroke dash animation.
   *
   * @type {string}
   * @optional
   * @default '0'
   * @example '0'
   */
  strokeDashOffsetEnd?: string;

  /**
   * Width of the stroke line.
   * @description Determines the thickness of the path's stroke in pixels.
   *
   * @type {string}
   * @optional
   * @default '2'
   * @example '2'
   */
  strokeWidth?: string;
}

//#endregion

//region INTERFACE ISharedLoadingG
/**
 * ### ISharedLoadingG
 * Structure representing a complete "G"-shaped loading indicator with two animated sections.
 *
 * @interface ISharedLoadingG
 * @extends Object
 * @defaults {@link DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE}
 * @description Encapsulates both the `topSection` and `bottomSection` animations along with
 *              optional container styling attributes. Designed to be rendered as an animated SVG
 *              or canvas-based element, the structure ensures consistent styling and animation
 *              across both parts of the "G".
 *
 * @example
 * const loadingG: ISharedLoadingG = {
 *   bottomSection: { ... },
 *   topSection: { ... },
 *   containerBackgroundColor: 'transparent',
 *   containerPadding: 20
 * };
 */
export interface ISharedLoadingG extends Object {
  /**
   * Configuration for the lower part of the "G".
   * @description Includes animation and stroke properties specific to the bottom arc.
   *
   * @type {ISharedLoadingGAnimation}
   * @example { strokeDashArray: 1000, strokeDashOffset: 1000, ... }
   */
  bottomSection: ISharedLoadingGAnimation;

  /**
   * Configuration for the upper part of the "G".
   * @description Includes animation and stroke properties specific to the top arc.
   *
   * @type {ISharedLoadingGAnimation}
   * @example { strokeDashArray: 1000, strokeDashOffset: 1000, ... }
   */
  topSection: ISharedLoadingGAnimation;

  /**
   * Background color of the animation container.
   * @description Optional styling override for container background.
   *
   * @type {string}
   * @optional
   * @default 'transparent'
   * @example '#ffffff'
   */
  containerBackgroundColor?: string;

  /**
   * Padding around the container in pixels.
   * @description Adds space around the animated content inside its container.
   *
   * @type {number}
   * @optional
   * @default 20
   * @example 20
   */
  containerPadding?: number;
}

//#endregion

//#region ANIMATIONS

// EXAMPLES OF THE SHARED LOADING G

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE
export const DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '2s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '2px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '2s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '2px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_NEON
export const DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_NEON: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE_NEON,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-text-rgba-50)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '4px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE_NEON,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-primary-rgba-30)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '4px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_NEON_FLASH
export const DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_NEON_FLASH: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE_NEON_FLASH,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-text-rgba-50)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '4px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE_NEON_FLASH,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-primary-rgba-30)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '4px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 10,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_ONCE
export const DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_ONCE: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '2px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '2px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_IN_SEQUENCE
export const DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_IN_SEQUENCE: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE,
    delay: '2s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '2s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '2px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '2s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '2px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_ONCE_IN_SEQUENCE
export const DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_ONCE_IN_SEQUENCE: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '1s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '3s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: AnimationIterationCountEnum.INFINITE, // or 1
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '2px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: AnimationIterationCountEnum.INFINITE, // or 1
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '2px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_TOP_THEN_BOTTOM
export const DEFAULT_LOADING_G_ANIMATION_DRAW_REVERSER_TOP_THEN_BOTTOM: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '4s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '2px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DRAW_REVERSE_ONCE,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: 1,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '2px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

// EXAMPLES OF THE DASH RUNNER

//#region CONST DEFAULT_LOADING_G_ANIMATION_DASH_RUNNER
export const DEFAULT_LOADING_G_ANIMATION_DASH_RUNNER: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DASH_RUNNER,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '2s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '4px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DASH_RUNNER,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '4px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#region CONST DEFAULT_LOADING_G_ANIMATION_DASH_RUNNER_NEON
export const DEFAULT_LOADING_G_ANIMATION_DASH_RUNNER_NEON: Readonly<ISharedLoadingG> = {
  bottomSection: {
    animation: AnimationLoadingGNameEnum.DASH_RUNNER_NEON,
    delay: '1s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '3s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-text-rgba-50)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-primary)',
    strokeWidth: '4px',
  },
  topSection: {
    animation: AnimationLoadingGNameEnum.DASH_RUNNER_NEON,
    delay: '0s',
    direction: AnimationDirectionEnum.NORMAL,
    duration: '4s',
    fill: AnimationFillModeEnum.FORWARDS,
    fillColour: 'var(--global-colour-primary-rgba-30)',
    iterationCount: AnimationIterationCountEnum.INFINITE,
    playState: AnimationPlayStateEnum.RUNNING,
    timingFunction: AnimationTimingFunctionsEnum.EASE,
    strokeDashArray: '1000',
    strokeDashOffset: '1000',
    strokeDashOffsetEnd: '1000',
    strokeColour: 'var(--global-colour-text)',
    strokeWidth: '4px',
  },
  containerBackgroundColor: 'transparent',
  containerPadding: 20,
};

//#endregion

//#endregion

// THE ANIMATION WE ARE USING

//#region CONST DEFAULT_LOADING_G_ANIMATION
export const DEFAULT_LOADER_FLASH = DEFAULT_LOADING_G_ANIMATION_DRAW_ONCE_NEON_FLASH;
export const DEFAULT_LOADER_INFINITE = DEFAULT_LOADING_G_ANIMATION_DASH_RUNNER_NEON;
//#endregion

